import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertCircle, XCircle, RefreshCw, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import LeadScenarioForm from './LeadScenarioForm';

const STYLES = {
  eligible: { label: 'Eligible', cls: 'bg-green-100 text-green-800 border-green-300', Icon: CheckCircle2 },
  potential: { label: 'Potentially Eligible — More Information Needed', cls: 'bg-amber-100 text-amber-800 border-amber-300', Icon: AlertCircle },
  ineligible: { label: 'Not Eligible', cls: 'bg-red-100 text-red-800 border-red-300', Icon: XCircle },
};
const CHECK_CLS = { pass: 'text-green-700', fail: 'text-red-700', partial: 'text-amber-700', unknown: 'text-zinc-500' };

const ResultCard = ({ r, selected, onToggle, onTermSheet }) => {
  const [open, setOpen] = useState(r.classification !== 'ineligible');
  const s = STYLES[r.classification];
  const key = `${r.lender_id}-${r.program_id || 'general'}`;
  return (
    <div className={`border rounded-lg p-3 ${selected ? 'ring-2 ring-primary/40' : ''}`} data-testid={`elig-${key}`}>
      <div className="flex items-start gap-3">
        <Checkbox checked={selected} onCheckedChange={onToggle} className="mt-1" data-testid={`elig-select-${key}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium">{r.lender_name}</p>
            <Badge variant="outline" className={s.cls}><s.Icon className="h-3 w-3 mr-1" />{s.label}</Badge>
            <span className="text-xs text-muted-foreground">{r.score}% criteria passed</span>
            {r.program_status !== 'approved' && <Badge variant="outline" className="text-xs">No approved program</Badge>}
          </div>
          <p className="text-sm text-muted-foreground">{r.program_name}{r.program_source === 'notes' && <Badge variant="outline" className="ml-2 text-[10px]">from Notes</Badge>}</p>
          {r.explanation && <p className="text-xs mt-1" data-testid={`elig-explanation-${key}`}>{r.explanation}</p>}
          {r.guideline_excerpt && <p className="text-[11px] text-muted-foreground italic mt-0.5">Guideline: “{r.guideline_excerpt}”</p>}
          {r.missing_borrower_info?.length > 0 && (
            <p className="text-xs text-amber-700 mt-1">Need from borrower: {r.missing_borrower_info.join(', ')}</p>
          )}
          {r.not_confirmed_lender_info?.length > 0 && (
            <p className="text-xs text-zinc-500 mt-0.5">Lender not confirmed: {r.not_confirmed_lender_info.join(', ')}</p>
          )}
          <button type="button" className="text-xs text-primary mt-1 flex items-center gap-1" onClick={() => setOpen(!open)} data-testid={`elig-toggle-${key}`}>
            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} {open ? 'Hide' : 'Show'} reasons
          </button>
          {open && (
            <ul className="mt-1 space-y-0.5 text-xs">
              {r.checks.map((c) => (
                <li key={c.criterion} className={CHECK_CLS[c.status]}>
                  <span className="font-medium">{c.criterion}:</span> {c.detail}
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={onTermSheet} disabled={r.classification === 'ineligible'} data-testid={`elig-termsheet-${key}`}>
          <FileText className="h-3.5 w-3.5 mr-1" /> Term sheet
        </Button>
      </div>
    </div>
  );
};

// Deterministic lender eligibility for one lead with manual selection/override and term-sheet handoff.
export const LeadEligibilityPanel = ({ lead, onLeadUpdated }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const res = await api.get(`/leads/${lead.lead_id}/eligibility`);
    setData(res.data.evaluated ? res.data : null);
    const sel = {};
    (res.data.selected_lenders || []).forEach((s) => { sel[`${s.lender_id}-${s.program_id || 'general'}`] = s; });
    setSelected(sel);
  };
  useEffect(() => { load().catch(() => setData(null)); }, [lead.lead_id]); // eslint-disable-line

  const scenarios = lead.construction?.scenarios || [];
  const [scenarioId, setScenarioId] = useState(lead.construction?.active_scenario_id || null);
  const run = async (sid = scenarioId) => {
    setBusy(true);
    try {
      const res = await api.post(`/leads/${lead.lead_id}/eligibility`, null, { params: sid ? { scenario_id: sid } : {} });
      setData(res.data);
      toast.success(`Checked ${res.data.results.length} lender programs: ${res.data.summary.eligible} eligible, ${res.data.summary.potential} potential`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Matching failed');
    } finally {
      setBusy(false);
    }
  };

  const saveSelection = async (next) => {
    const selections = Object.values(next).map((s) => ({ lender_id: s.lender_id, program_id: s.program_id || null, override: s.classification !== 'eligible', reason: s.classification !== 'eligible' ? 'Manual override' : null }));
    try {
      await api.put(`/leads/${lead.lead_id}/selected-lenders`, { selections });
      toast.success(selections.length ? `${selections.length} lender(s) selected` : 'Selection cleared');
      onLeadUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save selection');
    }
  };

  const toggle = (r) => {
    const key = `${r.lender_id}-${r.program_id || 'general'}`;
    const next = { ...selected };
    if (next[key]) delete next[key]; else next[key] = r;
    setSelected(next);
    saveSelection(next);
  };

  const prepareTermSheet = async (r) => {
    const amount = window.prompt('Loan amount for this term sheet (USD):', data?.profile?.loan_amount_max || '');
    if (!amount) return;
    try {
      const res = await api.post('/term-sheets', {
        lead_id: lead.lead_id, lender_id: r.lender_id, program_id: r.program_id || null,
        loan_amount: Number(String(amount).replace(/[^0-9.]/g, '')), property_address: lead.property_address || 'Not provided',
      });
      toast.success('Draft term sheet created — review, edit and approve before sending');
      navigate(`/term-sheets?open=${res.data.term_sheet_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create term sheet');
    }
  };

  const groups = ['eligible', 'potential', 'ineligible'];
  return (
    <div className="space-y-3" data-testid="lead-eligibility-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {data ? <>Evaluated {new Date(data.evaluated_at).toLocaleString()} · {data.summary.eligible} eligible · {data.summary.potential} potential · {data.summary.ineligible} ineligible</> : 'Not evaluated yet.'}
        </p>
        <Button size="sm" variant="outline" onClick={() => run()} disabled={busy} data-testid="lead-run-eligibility-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} /> {data ? 'Re-run matching' : 'Run lender matching'}
        </Button>
      </div>
      {scenarios.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="elig-scenario-picker">
          <span className="text-muted-foreground">Development scenario:</span>
          {scenarios.map((s) => (
            <Button key={s.scenario_id} size="sm" variant={s.scenario_id === scenarioId ? 'default' : 'outline'} className="h-7" onClick={() => { setScenarioId(s.scenario_id); run(s.scenario_id); }} data-testid={`elig-scenario-${s.scenario_id}`}>{s.name}</Button>
          ))}
        </div>
      )}
      {!lead.construction && <LeadScenarioForm key={lead.lead_id} lead={lead} onSaved={async () => { await run(); onLeadUpdated?.(); }} />}
      {lead.construction && <p className="text-xs text-muted-foreground" data-testid="elig-construction-note">Using the structured Construction Loan Details{data?.profile?.scenario_name ? ` — scenario “${data.profile.scenario_name}”` : ''}. Edit the lead to change inputs.</p>}
      {data?.profile && (
        <p className="text-xs text-muted-foreground" data-testid="lead-eligibility-profile">
          Borrower inputs used: amount {data.profile.loan_amount_min != null ? `$${Number(data.profile.loan_amount_min).toLocaleString()}` : '?'}–{data.profile.loan_amount_max != null ? `$${Number(data.profile.loan_amount_max).toLocaleString()}` : '?'} · FICO {data.profile.fico_min ?? '?'}{data.profile.fico_max && data.profile.fico_max !== data.profile.fico_min ? `–${data.profile.fico_max}` : '+'} · {data.profile.property_type || 'property type ?'} · {data.profile.state || 'state ?'} · {data.profile.loan_purpose || 'purpose ?'} {data.profile.construction ? '' : ` · LTV ${data.profile.ltv != null ? `${data.profile.ltv}%` : 'unknown (no property value)'}`}{data.profile.total_project_cost != null ? ` · total project cost $${Number(data.profile.total_project_cost).toLocaleString()}` : ''}{data.profile.units != null ? ` · ${data.profile.units} units` : ''}{data.profile.entity ? ` · ${data.profile.entity}` : ''}{data.profile.ltc != null ? ` · LTC ${data.profile.ltc}%` : ''}{data.profile.ltarv != null ? ` · LTARV ${data.profile.ltarv}%` : ''}{data.profile.experience != null ? ` · ${data.profile.experience} projects` : ''}{data.profile.liquidity != null ? ` · liquidity $${Number(data.profile.liquidity).toLocaleString()}` : ''}
        </p>
      )}
      {data && groups.map((g) => {
        const items = data.results.filter((r) => r.classification === g);
        if (!items.length) return null;
        return (
          <div key={g} className="space-y-2" data-testid={`elig-group-${g}`}>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{STYLES[g].label} ({items.length})</p>
            {items.map((r) => {
              const key = `${r.lender_id}-${r.program_id || 'general'}`;
              return <ResultCard key={key} r={r} selected={!!selected[key]} onToggle={() => toggle(r)} onTermSheet={() => prepareTermSheet(r)} />;
            })}
          </div>
        );
      })}
    </div>
  );
};

export default LeadEligibilityPanel;
